import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';

// Currency configurations
export const CURRENCIES = {
  USD: {
    code: 'USD',
    symbol: '$',
    name: 'US Dollar',
    locale: 'en-US'
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    name: 'Euro',
    locale: 'de-DE'
  }
};

const CurrencyContext = createContext();

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => {
    // Load saved preference from localStorage
    const saved = localStorage.getItem('tradehub_currency');
    return saved && CURRENCIES[saved] ? saved : 'USD';
  });
  
  // Exchange rate: how many EUR per 1 USD (e.g., 0.92 means $1 = €0.92)
  const [eurRate, setEurRate] = useState(0.92);
  const [rateLoaded, setRateLoaded] = useState(false);
  const [rateSource, setRateSource] = useState(null);
  const [rateUpdatedAt, setRateUpdatedAt] = useState(null);

  // Fetch exchange rate from backend on mount
  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        const response = await axios.get(`${API}/settings/exchange-rate`);
        if (response.data.eur_rate) {
          setEurRate(response.data.eur_rate);
          setRateSource(response.data.source);
          setRateUpdatedAt(response.data.updated_at);
        }
      } catch (error) {
        console.error('Failed to fetch exchange rate, using default:', error);
      } finally {
        setRateLoaded(true);
      }
    };
    fetchExchangeRate();
  }, []);

  useEffect(() => {
    // Save preference to localStorage
    localStorage.setItem('tradehub_currency', currency);
  }, [currency]);

  // Convert USD amount to the selected currency
  const convertAmount = (usdAmount) => {
    if (usdAmount === null || usdAmount === undefined) return 0;
    if (currency === 'USD') return usdAmount;
    // Convert to EUR: multiply by the rate
    return usdAmount * eurRate;
  };

  // Format amount in current currency (with conversion from USD)
  const formatPrice = (usdAmount, showCode = false) => {
    if (usdAmount === null || usdAmount === undefined) return '';
    
    const currencyConfig = CURRENCIES[currency];
    const convertedAmount = convertAmount(usdAmount);
    
    const formatted = new Intl.NumberFormat(currencyConfig.locale, {
      style: 'currency',
      currency: currencyConfig.code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(convertedAmount);
    
    return showCode ? `${formatted} ${currencyConfig.code}` : formatted;
  };

  // Get the converted amount (for PayPal payments)
  const getConvertedAmount = (usdAmount) => {
    return Math.round(convertAmount(usdAmount) * 100) / 100; // Round to 2 decimal places
  };

  // Get current currency symbol
  const getCurrencySymbol = () => CURRENCIES[currency].symbol;

  // Get current currency code
  const getCurrencyCode = () => CURRENCIES[currency].code;

  // Get current currency config
  const getCurrencyConfig = () => CURRENCIES[currency];

  const value = {
    currency,
    setCurrency,
    formatPrice,
    convertAmount,
    getConvertedAmount,
    getCurrencySymbol,
    getCurrencyCode,
    getCurrencyConfig,
    eurRate,
    rateLoaded,
    rateSource,
    rateUpdatedAt,
    availableCurrencies: Object.keys(CURRENCIES)
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export default CurrencyContext;
